import React, { useMemo } from "react"

import { isEqual } from "lodash-es"
import { FormProvider } from "react-hook-form"

import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import type { PolicyColumnKey } from "@common-shared/config/oneVerify/oneVerifyPoliciesColumns.config.ts"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config.ts"
import { ButtonElement } from "@front-shared/elements/button.element.tsx"
import { DatePickerElement } from "@front-shared/elements/datePicker.element.tsx"
import { NirvanaBreathingLoaderElement } from "@front-shared/elements/nirvanaBreathingLoader.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper.tsx"

import { GENERAL_CONFIG } from "../../../../config/general.config"
import { OVERRIDES_CONFIG } from "../../../../config/overrides.config"
import { CoverageHistoryHelper } from "../../../../helpers/coverageHistory.helper"
import { useBulkOverrides } from "../../../../hooks/bulkOverrides.hooks"
import { useAppDispatch } from "../../../../store/appDispatch.hook"
import { useAppSelector } from "../../../../store/selectors/app.selector"
import { selectedPracticeRoleSelector } from "../../../../store/selectors/selectedPracticeRole.selector"
import { createCoverageOverrides } from "../../../../store/thunks/coverageOverrides.thunks"
import { policiesReloadList } from "../../../../store/thunks/policies.thunks"
import type {
    ICoverageOverrideResult,
    IOverrideDefaultData,
    IOverrideUpsertRequestRawData,
} from "../../../../types/data.types.ts"
import { BulkOverridesFormFieldComponent } from "./bulkOverridesFormField.component"

export const BulkOverridesFormComponent: React.FunctionComponent<
    IBulkOverridesFormComponentProps
> = props => {
    const {
        overridesFormMethods,
        getFormDefaultData,
        dataWatcher,
        overridesFormKey,
        onResetOverride,
        isCreateOverrideLoading,
        minOverrideExpirationDate,
        suggestedOverrideExpirationDate,
        onUseSuggestedOverrideExpiration,
        mapCreationFormData,
        validateOverrideCreation,
    } = useBulkOverrides()

    const dispatch = useAppDispatch()
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const formItemHasValue = (item: any): boolean => {
        return item !== null && typeof item !== "undefined" && item !== ""
    }

    const defaultData = useMemo<IOverrideDefaultData>(() => {
        // Initially we get first's coverage check as a base
        // Then loop through all needed coverage checks and compare values
        // Record if multiple etc
        const finalResult: IOverrideDefaultData = {
            // Combination of override and coverage result
            form: getFormDefaultData(
                null,
                props.coverageChecks[0]?.coverageResult || undefined,
            ),
            // Just from override - always will be blank values for bulk
            override: getFormDefaultData(null, undefined, false),
            bulkMultipleValues: {},
        }

        // Start from 2nd
        for (let i = 1; i < props.coverageChecks.length; i++) {
            const newFormResult = getFormDefaultData(
                null,
                props.coverageChecks[i]?.coverageResult || undefined,
            )

            for (const key of Object.keys(
                OVERRIDES_CONFIG.bulkOverrides.allowedOverrideFields,
            )) {
                const fieldKey = key as PolicyColumnKey

                const formTarget =
                    OVERRIDES_CONFIG.bulkOverrides.allowedOverrideFields[
                        fieldKey
                    ]

                if (
                    OVERRIDES_CONFIG.bulkOverrides.ignoredValueComputationFields.includes(
                        fieldKey,
                    )
                ) {
                    // Clean data in final result for this key
                    if (formTarget) {
                        finalResult.form[formTarget] = undefined
                    }

                    // Clean data in differences for this key
                    if (finalResult.bulkMultipleValues) {
                        finalResult.bulkMultipleValues[fieldKey] = undefined
                    }

                    continue
                }

                const newFormValue = formTarget
                    ? newFormResult[formTarget]
                    : undefined

                // If no value - just do nothing and continue
                if (!formItemHasValue(newFormValue)) {
                    continue
                }

                const finalResultFormValue = formTarget
                    ? finalResult.form[formTarget]
                    : undefined

                // Check if current value is different from final result
                // Different - delete value in form
                // Check if this difference already in diff log
                // if same do nothing and just continue
                if (!isEqual(newFormValue, finalResultFormValue)) {
                    finalResult.bulkMultipleValues =
                        finalResult.bulkMultipleValues || {}
                    finalResult.bulkMultipleValues[fieldKey] =
                        finalResult.bulkMultipleValues[fieldKey] || []

                    // Clean data in final result for this key
                    // And push it to diff log
                    if (formTarget) {
                        if (
                            formItemHasValue(finalResultFormValue) &&
                            !finalResult.bulkMultipleValues[fieldKey]?.includes(
                                finalResultFormValue,
                            )
                        ) {
                            finalResult.bulkMultipleValues[fieldKey]?.push(
                                finalResultFormValue,
                            )
                        }

                        finalResult.form[formTarget] = undefined
                    }

                    if (
                        !finalResult.bulkMultipleValues[fieldKey]?.includes(
                            newFormValue,
                        )
                    ) {
                        finalResult.bulkMultipleValues[fieldKey]?.push(
                            newFormValue,
                        )
                    }
                }
            }
        }

        return finalResult
    }, [props.coverageChecks])

    // Monitor difference of default data and dataWatcher
    // If default form was changed -> activate save button
    const isFormSubmitDisabled = useMemo<boolean>(() => {
        return (
            JSON.stringify(defaultData.form) === JSON.stringify(dataWatcher) ||
            !dataWatcher.expirationDate
        )
    }, [JSON.stringify(defaultData.form), JSON.stringify(dataWatcher)])

    const onOverrideDone = (result: ICoverageOverrideResult) => {
        const successfulOverride = result.success[0]

        if (result.errors.length || !successfulOverride) {
            result.errors.forEach(item => {
                ToastrHelper.error(item.msg, undefined, true)
            })

            return
        }

        ToastrHelper.success("Overrides were successfully created")

        if (selectedPracticeRole && props.listType) {
            dispatch(
                policiesReloadList({
                    practice: selectedPracticeRole.practice,
                    payload: {
                        policiesViewType: props.listType,
                    },
                }),
            )
        }

        props.onDone()
    }

    const onSaveOverride = (data: any) => {
        if (!selectedPracticeRole) {
            return
        }

        const typedData = mapCreationFormData(data, defaultData)

        const validationResult = validateOverrideCreation(typedData)

        if (!validationResult) {
            return
        }

        const payloadData: IOverrideUpsertRequestRawData[] = []

        for (const coverageCheck of props.coverageChecks) {
            const coverageCheckIdentificationData =
                CoverageHistoryHelper.getPolicyIdentificationData(coverageCheck)

            if (!coverageCheckIdentificationData) {
                continue
            }

            payloadData.push({
                policyIdentification: {
                    ...coverageCheckIdentificationData,
                    network:
                        CoverageHistoryHelper.getCoverageCheckNetwork(
                            coverageCheck,
                        ),
                },
                formData: typedData,
                currentRequestId: coverageCheck.coverageResult.resultId,
            })
        }

        dispatch(
            createCoverageOverrides({
                practice: selectedPracticeRole.practice,
                payload: {
                    data: payloadData,
                    sleepAtTheEndSeconds:
                        OVERRIDES_CONFIG.bulkOverrides.sleepAtTheEndSeconds,
                },
                onSuccess: onOverrideDone,
            }),
        )
    }

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
            key={overridesFormKey}
        >
            <NirvanaBreathingLoaderElement isActive={isCreateOverrideLoading} />

            <FormProvider {...overridesFormMethods}>
                <div
                    className="
                        p-[24px] bg-brand-white rounded-[8px]
                        border border-solid border-brand-offWhite
                    "
                >
                    <PrimaryText typography="h4">Overrides</PrimaryText>

                    <PrimaryText className="mt-[8px]">
                        Please enter the values you would like to override
                    </PrimaryText>

                    <div className="mt-[8px]">
                        <div className="flex gap-x-4 md:flex-col">
                            <div className="flex flex-col">
                                {Object.keys(
                                    OVERRIDES_CONFIG.bulkOverrides
                                        .allowedOverrideFields,
                                )
                                    .slice(0, 4)
                                    .map(key => {
                                        return (
                                            <BulkOverridesFormFieldComponent
                                                key={key}
                                                className="w-full mt-[8px]"
                                                fieldKey={
                                                    key as PolicyColumnKey
                                                }
                                                defaultData={defaultData}
                                                dataWatcher={dataWatcher}
                                                benefitStructureNotRequired={
                                                    props.benefitStructureNotRequired
                                                }
                                                formItemHasValue={
                                                    formItemHasValue
                                                }
                                            />
                                        )
                                    })}
                            </div>
                            <div className="flex flex-col">
                                {Object.keys(
                                    OVERRIDES_CONFIG.bulkOverrides
                                        .allowedOverrideFields,
                                )
                                    .slice(4)
                                    .map(key => {
                                        return (
                                            <BulkOverridesFormFieldComponent
                                                key={key}
                                                className="w-full mt-[8px]"
                                                fieldKey={
                                                    key as PolicyColumnKey
                                                }
                                                defaultData={defaultData}
                                                dataWatcher={dataWatcher}
                                                formItemHasValue={
                                                    formItemHasValue
                                                }
                                            />
                                        )
                                    })}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end mt-[16px] md:block">
                    <PrimaryText
                        typography="textSmall"
                        className="mr-[8px] min-w-0 flex-shrink-0"
                    >
                        Overrides Expiration Date
                    </PrimaryText>

                    <div className="relative mr-[16px] md:mr-[0px] md:max-w-[200px]">
                        <DatePickerElement
                            name="expirationDate"
                            size="small"
                            defaultValue={
                                dataWatcher.expirationDate || undefined
                            }
                            reactHookFormValidations={{
                                required: FRONT_VALIDATION_CONFIG.required,
                            }}
                            shouldValidate
                            reactHookFormErrors={
                                overridesFormMethods.formState.errors
                            }
                            reactHookFormRegister={
                                overridesFormMethods.register
                            }
                            reactHookFormUnregister={
                                overridesFormMethods.unregister
                            }
                            reactHookFormSet={overridesFormMethods.setValue}
                            minDate={minOverrideExpirationDate.toDate()}
                            disabled={isCreateOverrideLoading}
                        />

                        {!dataWatcher.expirationDate ? (
                            <PrimaryText
                                typography="caption"
                                className="absolute bottom-[-20px] whitespace-nowrap"
                            >
                                Suggested:{" "}
                                <span
                                    className="cursor-pointer text-brand-vibrantPurple underline text-bold"
                                    onClick={onUseSuggestedOverrideExpiration}
                                >
                                    {suggestedOverrideExpirationDate.format(
                                        GENERAL_CONFIG.defaultMomentDateFormat,
                                    )}
                                </span>
                            </PrimaryText>
                        ) : null}
                    </div>

                    <div className="flex items-center md:mt-[40px]">
                        <ButtonElement
                            buttonClassName="w-[100px]"
                            htmlType="button"
                            type="default"
                            label="Reset"
                            disabled={isCreateOverrideLoading}
                            onClick={() => {
                                onResetOverride()
                            }}
                        />

                        <ButtonElement
                            className="ml-[16px]"
                            buttonClassName="w-[100px]"
                            htmlType="button"
                            type="primary"
                            label="Save"
                            disabled={isFormSubmitDisabled}
                            isLoading={isCreateOverrideLoading}
                            onClick={overridesFormMethods.handleSubmit(
                                onSaveOverride,
                            )}
                        />
                    </div>
                </div>
            </FormProvider>
        </div>
    )
}

interface IBulkOverridesFormComponentProps {
    className?: string
    coverageChecks: IOneVerifyCoverageCheckHistory[]
    onDone: () => void
    listType?: PoliciesListType // Will be used to refetch list, to identify which one
    benefitStructureNotRequired?: boolean
}
